/**
 * Assembly Mode
 *
 * Free-form canvas for composing rotated components.
 * Unlike the grid mode, shapes can be placed at any position
 * and rotated in 60° increments.
 */

import type { Point, Triangle } from './types';
import { TRINITY_PALETTE } from './types';

export interface AssemblyTriangle {
  vertices: [Point, Point, Point];
  color: string;
  rotation: number;  // 0, 60, 120, 180, 240, 300
}

export interface AssemblyComponent {
  id: string;
  name: string;
  triangles: AssemblyTriangle[];
  anchor: Point;  // Reference point for placement
}

export interface PlacedComponent {
  id: string;
  componentId: string;
  position: Point;
  rotation: number;  // 0, 60, 120, 180, 240, 300
  triangles: AssemblyTriangle[];  // Computed triangles after transform
}

/**
 * Rotate a point around an origin by degrees
 */
export function rotatePoint(point: Point, origin: Point, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const dx = point.x - origin.x;
  const dy = point.y - origin.y;

  return {
    x: origin.x + dx * cos - dy * sin,
    y: origin.y + dx * sin + dy * cos,
  };
}

/**
 * Rotate a triangle around an origin
 */
export function rotateTriangle(
  triangle: AssemblyTriangle,
  origin: Point,
  degrees: number
): AssemblyTriangle {
  return {
    ...triangle,
    vertices: [
      rotatePoint(triangle.vertices[0], origin, degrees),
      rotatePoint(triangle.vertices[1], origin, degrees),
      rotatePoint(triangle.vertices[2], origin, degrees),
    ],
    rotation: (triangle.rotation + degrees) % 360,
  };
}

/**
 * Translate a triangle by offset
 */
export function translateTriangle(
  triangle: AssemblyTriangle,
  offset: Point
): AssemblyTriangle {
  return {
    ...triangle,
    vertices: [
      { x: triangle.vertices[0].x + offset.x, y: triangle.vertices[0].y + offset.y },
      { x: triangle.vertices[1].x + offset.x, y: triangle.vertices[1].y + offset.y },
      { x: triangle.vertices[2].x + offset.x, y: triangle.vertices[2].y + offset.y },
    ],
  };
}

/**
 * Create an equilateral triangle at origin pointing up
 */
export function createEquilateralTriangle(
  sideLength: number,
  color: string
): AssemblyTriangle {
  const height = (sideLength * Math.sqrt(3)) / 2;

  return {
    vertices: [
      { x: 0, y: -height * (2 / 3) },           // Top
      { x: -sideLength / 2, y: height / 3 },    // Bottom-left
      { x: sideLength / 2, y: height / 3 },     // Bottom-right
    ],
    color,
    rotation: 0,
  };
}

/**
 * Create a rhombus (2 triangles) - the fundamental Trinity shape
 */
export function createAssemblyRhombus(
  sideLength: number,
  topColor: string,
  bottomColor: string
): AssemblyTriangle[] {
  const height = (sideLength * Math.sqrt(3)) / 2;

  // Up triangle
  const upTriangle: AssemblyTriangle = {
    vertices: [
      { x: 0, y: -height },           // Top
      { x: -sideLength / 2, y: 0 },   // Bottom-left
      { x: sideLength / 2, y: 0 },    // Bottom-right
    ],
    color: topColor,
    rotation: 0,
  };

  // Down triangle
  const downTriangle: AssemblyTriangle = {
    vertices: [
      { x: 0, y: height },            // Bottom
      { x: sideLength / 2, y: 0 },    // Top-right
      { x: -sideLength / 2, y: 0 },   // Top-left
    ],
    color: bottomColor,
    rotation: 180,
  };

  return [upTriangle, downTriangle];
}

/**
 * Create a Trinity blade (3 rhombuses = 6 triangles)
 * This is one arm of the Trinity logo
 */
export function createTrinityBlade(sideLength: number): AssemblyComponent {
  const height = (sideLength * Math.sqrt(3)) / 2;
  const { lightBlue, mediumBlue, darkBlue } = TRINITY_PALETTE;

  const triangles: AssemblyTriangle[] = [];

  // Rhombus 1 (top) - light blue
  const rhombus1 = createAssemblyRhombus(sideLength, lightBlue, lightBlue);
  rhombus1.forEach((t) => {
    triangles.push(translateTriangle(t, { x: 0, y: -height * 2 }));
  });

  // Rhombus 2 (middle) - medium blue
  const rhombus2 = createAssemblyRhombus(sideLength, mediumBlue, mediumBlue);
  rhombus2.forEach((t) => {
    triangles.push(translateTriangle(t, { x: 0, y: 0 }));
  });

  // Rhombus 3 (bottom) - dark blue
  const rhombus3 = createAssemblyRhombus(sideLength, darkBlue, darkBlue);
  rhombus3.forEach((t) => {
    triangles.push(translateTriangle(t, { x: 0, y: height * 2 }));
  });

  return {
    id: 'trinity-blade',
    name: 'Trinity Blade',
    triangles,
    anchor: { x: 0, y: 0 },
  };
}

/**
 * Create the complete Trinity logo (3 blades rotated 120° apart)
 */
export function createAssemblyTrinityLogo(sideLength: number): AssemblyComponent {
  const blade = createTrinityBlade(sideLength);
  const height = (sideLength * Math.sqrt(3)) / 2;

  const allTriangles: AssemblyTriangle[] = [];

  // Blade 1: pointing up (0°)
  const blade1Offset: Point = { x: -sideLength * 0.75, y: -height };
  blade.triangles.forEach((t) => {
    allTriangles.push(translateTriangle(t, blade1Offset));
  });

  // Blade 2: rotated 120° (pointing bottom-right)
  const blade2Offset: Point = { x: sideLength * 0.75, y: -height };
  blade.triangles.forEach((t) => {
    const rotated = rotateTriangle(t, { x: 0, y: 0 }, 120);
    allTriangles.push(translateTriangle(rotated, blade2Offset));
  });

  // Blade 3: rotated 240° (pointing bottom-left)
  const blade3Offset: Point = { x: 0, y: height * 2 };
  blade.triangles.forEach((t) => {
    const rotated = rotateTriangle(t, { x: 0, y: 0 }, 240);
    allTriangles.push(translateTriangle(rotated, blade3Offset));
  });

  return {
    id: 'trinity-logo',
    name: 'Trinity Logo',
    triangles: allTriangles,
    anchor: { x: 0, y: 0 },
  };
}

/**
 * Assembly Canvas - manages placed components
 */
export class AssemblyCanvas {
  private placedComponents: PlacedComponent[] = [];
  private nextId = 1;
  private sideLength: number;

  constructor(sideLength: number = 50) {
    this.sideLength = sideLength;
  }

  /**
   * Place a component at a position with rotation
   */
  place(
    component: AssemblyComponent,
    position: Point,
    rotation: number = 0
  ): PlacedComponent {
    // Transform all triangles
    const transformedTriangles = component.triangles.map((t) => {
      // First rotate around anchor
      const rotated = rotateTriangle(t, component.anchor, rotation);
      // Then translate to position
      return translateTriangle(rotated, position);
    });

    const placed: PlacedComponent = {
      id: `placed-${this.nextId++}`,
      componentId: component.id,
      position,
      rotation,
      triangles: transformedTriangles,
    };

    this.placedComponents.push(placed);
    return placed;
  }

  /**
   * Remove a placed component
   */
  remove(id: string): boolean {
    const index = this.placedComponents.findIndex((p) => p.id === id);
    if (index >= 0) {
      this.placedComponents.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Get all placed components
   */
  getPlaced(): PlacedComponent[] {
    return [...this.placedComponents];
  }

  /**
   * Get all triangles from all placed components
   */
  getAllTriangles(): AssemblyTriangle[] {
    return this.placedComponents.flatMap((p) => p.triangles);
  }

  /**
   * Clear all placed components
   */
  clear(): void {
    this.placedComponents = [];
  }

  /**
   * Find closest snap point to a position
   */
  findSnapPoint(position: Point, snapDistance: number = 20): Point | null {
    let closest: Point | null = null;
    let minDist = snapDistance;

    for (const placed of this.placedComponents) {
      for (const triangle of placed.triangles) {
        for (const vertex of triangle.vertices) {
          const dist = Math.sqrt(
            (position.x - vertex.x) ** 2 + (position.y - vertex.y) ** 2
          );
          if (dist < minDist) {
            minDist = dist;
            closest = vertex;
          }
        }
      }
    }

    return closest;
  }

  /**
   * Export as SVG
   */
  exportSVG(width: number, height: number, backgroundColor: string = '#0f172a'): string {
    const triangles = this.getAllTriangles();

    const paths = triangles.map((t) => {
      const d = `M ${t.vertices[0].x} ${t.vertices[0].y} L ${t.vertices[1].x} ${t.vertices[1].y} L ${t.vertices[2].x} ${t.vertices[2].y} Z`;
      return `<path d="${d}" fill="${t.color}" stroke="#1e293b" stroke-width="1"/>`;
    });

    return `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  <rect width="100%" height="100%" fill="${backgroundColor}"/>
  <g transform="translate(${width / 2}, ${height / 2})">
    ${paths.join('\n    ')}
  </g>
</svg>`;
  }
}

/**
 * Get all available assembly components
 */
export function getAssemblyComponents(sideLength: number): AssemblyComponent[] {
  return [
    createTrinityBlade(sideLength),
    createAssemblyTrinityLogo(sideLength),
    {
      id: 'single-up',
      name: 'Triangle Up',
      triangles: [createEquilateralTriangle(sideLength, TRINITY_PALETTE.lightBlue)],
      anchor: { x: 0, y: 0 },
    },
    {
      id: 'single-down',
      name: 'Triangle Down',
      triangles: [rotateTriangle(
        createEquilateralTriangle(sideLength, TRINITY_PALETTE.lightBlue),
        { x: 0, y: 0 },
        180
      )],
      anchor: { x: 0, y: 0 },
    },
    {
      id: 'rhombus',
      name: 'Rhombus',
      triangles: createAssemblyRhombus(sideLength, TRINITY_PALETTE.lightBlue, TRINITY_PALETTE.darkBlue),
      anchor: { x: 0, y: 0 },
    },
  ];
}
